import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { ConfirmationMessage, ErrorMessage } from "@/components/ui/States";
import { BUSINESS } from "@/lib/constants";
import { SERVICES } from "@/data/services";

const URGENCY_OPTIONS = [
    { value: "routine", label: "Routine" },
    { value: "2-weeks", label: "Within 2 weeks" },
    { value: "this-week", label: "This week" },
    { value: "hoa-deadline", label: "HOA deadline" },
    { value: "storm-damaged", label: "Storm-damaged" },
];

const PROJECT_TYPES = [
    { value: "new-repair", label: "New repair" },
    { value: "insurance-storm", label: "Insurance / storm" },
    { value: "hoa-deadline", label: "HOA deadline" },
    { value: "property-manager", label: "Property manager" },
    { value: "other", label: "Other" },
];

const CONTACT_METHODS = [
    { value: "phone", label: "Phone" },
    { value: "text", label: "Text" },
    { value: "email", label: "Email" },
];

const BEST_TIMES = [
    { value: "morning", label: "Morning" },
    { value: "midday", label: "Midday" },
    { value: "afternoon", label: "Afternoon" },
    { value: "anytime", label: "Anytime (during 7 AM – 5 PM)" },
];

// Feb 2026 — John's verified service guidance
const PANEL_COUNT_OPTIONS = [
    { value: "unsure", label: "Not sure" },
    { value: "1-3", label: "1–3 panels (small repair)" },
    { value: "4-10", label: "4–10 panels (medium repair)" },
    { value: "11-20", label: "11–20 panels (large repair)" },
    { value: "20+", label: "More than 20 panels" },
    { value: "rescreen", label: "I may need a full rescreen" },
];

const ACCESS_DIFFICULTY_OPTIONS = [
    { value: "unsure", label: "Not sure" },
    { value: "ground-wall", label: "Ground-level wall panels" },
    { value: "roof", label: "Roof panels" },
    { value: "roof-over-pool", label: "Roof panels over the pool" },
    { value: "above-24ft-ladder", label: "Higher than a standard 24-foot ladder" },
    { value: "condo-highrise", label: "Condo / high-rise access" },
    { value: "other", label: "Other" },
];

// Job-detail subtypes shown when relevant top-level service is selected
const SCREEN_DOOR_DETAILS = [
    { value: "standard-screen-door", label: "Standard screen door replacement (~36\" × 80\")" },
    { value: "custom-screen-door", label: "Custom screen door (PCA Products)" },
    { value: "z-bar-trim", label: "Z-bar trim replacement" },
    { value: "screen-door-repair-other", label: "Other screen door repair" },
];

export default function EstimateForm({ variant = "long", presetService, source = "/free-estimate" }) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [city, setCity] = useState("Naples");
    const [address, setAddress] = useState("");
    const [serviceNeeded, setServiceNeeded] = useState(presetService || "");
    const [projectType, setProjectType] = useState("");
    const [urgencyInput, setUrgencyInput] = useState("");
    const [notes, setNotes] = useState("");
    const [preferredContact, setPreferredContact] = useState("");
    const [bestTime, setBestTime] = useState("");
    const [panelCount, setPanelCount] = useState("");
    const [accessDifficulty, setAccessDifficulty] = useState("");
    const [jobDetail, setJobDetail] = useState("");
    const [consent, setConsent] = useState(false);

    const [honeypot, setHoneypot] = useState("");
    const startMs = useRef(Date.now());

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [touchedFirst, setTouchedFirst] = useState(false);
    const [prefilled, setPrefilled] = useState(false);

    useEffect(() => {
        startMs.current = Date.now();
    }, []);

    // Prefill from the chat assistant handoff (sessionStorage, one-shot).
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("a1_estimate_prefill");
            if (!raw) return;
            sessionStorage.removeItem("a1_estimate_prefill");
            const p = JSON.parse(raw);
            if (p.name) setName(p.name);
            if (p.phone) setPhone(p.phone);
            if (p.email) setEmail(p.email);
            if (p.city) setCity(p.city);
            if (p.service_slug && SERVICES.some((s) => s.slug === p.service_slug)) setServiceNeeded(p.service_slug);
            if (p.description) setNotes(p.description);
            setPrefilled(true);
        } catch { /* ignore bad prefill payloads */ }
    }, []);

    const handleFirstFocus = () => {
        if (!touchedFirst) {
            setTouchedFirst(true);
            track(EVENTS.ESTIMATE_START, { variant, source });
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!phone.trim()) {
            setError("Please share a phone number so we can follow up.");
            return;
        }
        if (!consent) {
            setError("Please confirm you'd like us to contact you about your estimate.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: name.trim() || null,
                phone: phone.trim(),
                email: email.trim() || null,
                city: city.trim() || "Naples",
                address: address.trim() || null,
                service_needed: serviceNeeded || null,
                project_type: projectType || null,
                urgency_input: urgencyInput || null,
                notes: notes.trim() || null,
                preferred_contact: preferredContact || null,
                best_time: bestTime || null,
                panel_count: panelCount || null,
                access_difficulty: accessDifficulty || null,
                job_detail: jobDetail || null,
                consent_given: true,
                website_url: honeypot, // honeypot
                start_ms: startMs.current,
            };
            const res = await api.post("/leads/estimate", payload);
            track(EVENTS.ESTIMATE_SUBMIT, { variant, source, lead_id: res.data?.lead_id });
            setSuccess(res.data.confirmation_message);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Sorry — something went wrong. Please try again or call 239-434-7306.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return <ConfirmationMessage message={success} testId="estimate-success" />;
    }

    return (
        <form onSubmit={submit} className="grid gap-4" data-testid="estimate-form" noValidate>
            <p className="a1-helper">
                Prefer to call? <a href={`tel:${BUSINESS.phoneTel}`}>{BUSINESS.phone}</a> · {BUSINESS.hours}
            </p>

            {prefilled ? (
                <div
                    className="rounded-md px-4 py-3 text-sm"
                    style={{ background: "rgba(40, 216, 224, 0.07)", border: "1px solid var(--color-accent)" }}
                    data-testid="ef-prefill-note"
                    role="status"
                >
                    We filled in what you shared with our chat assistant — please double-check everything before sending.
                </div>
            ) : null}

            <div>
                <label className="a1-label" htmlFor="ef-name">Your Name {variant === "long" ? "(optional)" : "(optional)"}</label>
                <input
                    id="ef-name"
                    className="a1-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={handleFirstFocus}
                    placeholder="First and last"
                    autoComplete="name"
                    data-testid="ef-name"
                />
            </div>

            <div>
                <label className="a1-label" htmlFor="ef-phone">Phone Number <span className="text-[var(--color-danger)]">*</span> (required)</label>
                <input
                    id="ef-phone"
                    type="tel"
                    inputMode="tel"
                    className="a1-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onFocus={handleFirstFocus}
                    placeholder="Best number to reach you"
                    autoComplete="tel"
                    required
                    aria-required="true"
                    data-testid="ef-phone"
                />
            </div>

            <div>
                <label className="a1-label" htmlFor="ef-city">City</label>
                <input
                    id="ef-city"
                    className="a1-input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Naples"
                    autoComplete="address-level2"
                    data-testid="ef-city"
                />
            </div>

            <div>
                <label className="a1-label" htmlFor="ef-service">What needs fixed?</label>
                <select
                    id="ef-service"
                    className="a1-select"
                    value={serviceNeeded}
                    onChange={(e) => setServiceNeeded(e.target.value)}
                    data-testid="ef-service"
                >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                        <option key={s.slug} value={s.slug}>{s.name}</option>
                    ))}
                    <option value="other">Other</option>
                </select>
            </div>

            {variant === "long" ? (
                <>
                    <div>
                        <label className="a1-label" htmlFor="ef-email">Email (optional)</label>
                        <input
                            id="ef-email"
                            type="email"
                            inputMode="email"
                            className="a1-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            data-testid="ef-email"
                        />
                    </div>

                    <div>
                        <label className="a1-label" htmlFor="ef-address">Service address (optional)</label>
                        <input
                            id="ef-address"
                            className="a1-input"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Optional — we just need enough to know where you are"
                            autoComplete="street-address"
                            data-testid="ef-address"
                        />
                    </div>

                    <div>
                        <label className="a1-label" htmlFor="ef-project">Project type</label>
                        <select
                            id="ef-project"
                            className="a1-select"
                            value={projectType}
                            onChange={(e) => setProjectType(e.target.value)}
                            data-testid="ef-project"
                        >
                            <option value="">Select…</option>
                            {PROJECT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="a1-label" htmlFor="ef-urgency">How soon?</label>
                        <select
                            id="ef-urgency"
                            className="a1-select"
                            value={urgencyInput}
                            onChange={(e) => setUrgencyInput(e.target.value)}
                            data-testid="ef-urgency"
                        >
                            <option value="">Select…</option>
                            {URGENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>

                    {/* Feb 2026 — John's verified service guidance */}
                    <div>
                        <label className="a1-label" htmlFor="ef-panel-count">How many panels need repair? (optional)</label>
                        <select
                            id="ef-panel-count"
                            className="a1-select"
                            value={panelCount}
                            onChange={(e) => setPanelCount(e.target.value)}
                            data-testid="ef-panel-count"
                        >
                            <option value="">Select…</option>
                            {PANEL_COUNT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <p className="a1-helper mt-1">
                            Approximate guidance only. Final pricing confirmed after technician review.
                        </p>
                    </div>

                    <div>
                        <label className="a1-label" htmlFor="ef-access">Is any damage hard to access? (optional)</label>
                        <select
                            id="ef-access"
                            className="a1-select"
                            value={accessDifficulty}
                            onChange={(e) => setAccessDifficulty(e.target.value)}
                            data-testid="ef-access"
                        >
                            <option value="">Select…</option>
                            {ACCESS_DIFFICULTY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>

                    {serviceNeeded === "screen-door-repair" ? (
                        <div>
                            <label className="a1-label" htmlFor="ef-job-detail">Screen door details (optional)</label>
                            <select
                                id="ef-job-detail"
                                className="a1-select"
                                value={jobDetail}
                                onChange={(e) => setJobDetail(e.target.value)}
                                data-testid="ef-job-detail"
                            >
                                <option value="">Select…</option>
                                {SCREEN_DOOR_DETAILS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {jobDetail === "custom-screen-door" ? (
                                <p className="a1-helper mt-2" data-testid="ef-pca-helper">
                                    A1 Screening is a certified approved installer for PCA Products custom screen doors. You can explore options at{" "}
                                    <a href="https://pcaproducts.com" target="_blank" rel="noopener noreferrer">pcaproducts.com</a>. Final options and pricing are confirmed after technician review.
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    <div>
                        <label className="a1-label" htmlFor="ef-notes">Anything else? (optional)</label>
                        <textarea
                            id="ef-notes"
                            className="a1-textarea"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="What you've noticed, what worries you, what your HOA is asking for, etc."
                            data-testid="ef-notes"
                        />
                        <p className="a1-helper mt-2">
                            Have photos of the damage? <Link to="/upload-photos">Upload them for technician review</Link> — photos help us prepare before we follow up.
                        </p>
                    </div>

                    {phone.length >= 7 ? (
                        <>
                            <div>
                                <label className="a1-label" htmlFor="ef-contact">Best way to reach you</label>
                                <select
                                    id="ef-contact"
                                    className="a1-select"
                                    value={preferredContact}
                                    onChange={(e) => setPreferredContact(e.target.value)}
                                    data-testid="ef-contact"
                                >
                                    <option value="">Select…</option>
                                    {CONTACT_METHODS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="a1-label" htmlFor="ef-besttime">Best time to reach you</label>
                                <select
                                    id="ef-besttime"
                                    className="a1-select"
                                    value={bestTime}
                                    onChange={(e) => setBestTime(e.target.value)}
                                    data-testid="ef-besttime"
                                >
                                    <option value="">Select…</option>
                                    {BEST_TIMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </>
                    ) : null}
                </>
            ) : null}

            <label className="flex items-start gap-2 text-sm">
                <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1"
                    required
                    aria-required="true"
                    data-testid="ef-consent"
                />
                <span>
                    By submitting this form, you agree that A1 Screening may contact you by phone, text, or email about your service request. Message and data rates may apply. Message frequency varies. Reply STOP to opt out of texts. See our <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms">Terms</a>.
                </span>
            </label>

            {/* Honeypot — hidden from real users */}
            <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="a1-honeypot"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                name="website_url"
            />

            {error ? <ErrorMessage message={error} testId="ef-error" /> : null}

            <button type="submit" className="a1-btn-primary w-full" disabled={submitting} data-testid="ef-submit">
                {submitting ? "Sending…" : "Get My Free Estimate"}
            </button>

            <p className="a1-helper text-center">
                We'll get back to you during business hours, {BUSINESS.hours}. Or call <a href={`tel:${BUSINESS.phoneTel}`}>{BUSINESS.phone}</a>.
            </p>
        </form>
    );
}
